<template>
  <div>
    <v-data-table
      fixed-header
      show-expand
      id="products-live"
      class="pt-2 elevation-1 i-data-table"
      item-key="productCode"
      v-model:expanded="expandedProduct"
      :headers="headers"
      :height="tableHeight"
      :loading="loading"
      :items="products"
      :items-per-page="pageSize"
      :page="currentPage"
      :search="search"
    >
      <template #[`header.availabilityStates`]="{ header }">
        {{ header.text }}
        <v-icon
          class="i-tooltip-color pl-1 pb-1"
          small
          v-tooltip="'Click to make available or unavailable'"
        >
          mdi-help-circle-outline
        </v-icon>
      </template>

      <template #[`header.actions`]="{ header }">
        {{ header.text }}
        <v-icon
          class="i-tooltip-color pb-1"
          small
          v-tooltip="
            'Products with upcoming changes cannot be edited or archived'
          "
        >
          mdi-help-circle-outline
        </v-icon>
      </template>

      <template #[`header.variableWeightTargetInKg`]="{ header }">
        {{ header.text }}
        <v-icon
          class="i-tooltip-color pb-1"
          small
          v-tooltip="'Applies only to Variable Weight products'"
        >
          mdi-help-circle-outline
        </v-icon>
      </template>

      <!-- Added to attach ref to ui element so that scroll to product can be achieved -->
      <template #[`item.categoryProductHierarchy.category`]="{ item }">
        <span :ref="item.supplierItemId">{{
          item.categoryProductHierarchy.category
        }}</span>
      </template>

      <template #[`item.variableWeightTargetInKg`]="{ item }">
        <template v-if="item.variableWeightTargetInKg">{{
          `${item.variableWeightTargetInKg} KG`
        }}</template>
        <template v-else>-</template>
      </template>

      <template #[`item.shortDescription`]="{ item }">
        <div class="d-flex align-center">
          <span>{{ item.shortDescription }}</span>

          <v-chip
            v-for="(tag, index) in getPromoTags(item)"
            :key="index"
            small
            class="ml-2"
            :color="promotionTagColor(tag)"
            :text-color="tag === 'FG' ? 'white' : 'black'"
          >
            {{ tag }}
          </v-chip>
        </div>
      </template>

      <template #[`item.unitQty`]="{ item }">
        <template v-if="item.variableWeightTargetInKg">-</template>
        <template v-else>{{ item.unitQty }} {{ item.unit }}</template>
      </template>

      <template #[`item.availabilityStates`]="{ item }">
        <v-overflow-btn
          attach
          class="i-btn-block"
          :class="[
            {
              'p-btn-status--available':
                item.availabilityStates.length === item.rangedStates.length
            },
            {
              'p-btn-status--unavailable': item.availabilityStates.length === 0
            },
            {
              'p-btn-status--partially-available':
                item.availabilityStates.length &&
                item.availabilityStates.length < item.rangedStates.length
            }
          ]"
          dense
          flat
          hide-details
          :loading="updatingAvailability"
          multiple
          rounded
          single-line
          solo
          menu-props="rounded"
          :items="['__ALL__', ...item.rangedStates]"
          v-model="item.availabilityStates"
          @change="
            $emit('update-product-availability', {
              productCode: item.productCode,
              availabilityStates: item.availabilityStates
            })
          "
        >
          <template v-slot:selection="{ index }">
            <div class="v-select__selection text-no-wrap" v-if="index === 0">
              <template
                v-if="
                  item.availabilityStates.length === item.rangedStates.length
                "
              >
                Available
              </template>
              <template v-else-if="item.availabilityStates.length === 0">
                Unavailable
              </template>
              <template v-else>
                Available
                <span
                  class="availability-status-count grey--text text--darken-1"
                >
                  ({{ item.availabilityStates.length }} of
                  {{ item.rangedStates.length }})
                </span>
              </template>
            </div>
          </template>

          <template v-slot:prepend-inner>
            <div
              class="v-select__selection text-no-wrap"
              v-if="item.availabilityStates.length === 0"
            >
              Unavailable
            </div>
          </template>

          <template v-slot:item="{ item: state, attrs, on }">
            <v-list-item
              v-if="state === '__ALL__'"
              dense
              class="px-2 all-option"
              @click.stop="toggleAllStates(item)"
            >
              <v-list-item-action class="my-0 ml-0 mr-2">
                <v-checkbox
                  dense
                  :input-value="
                    item.availabilityStates.length === item.rangedStates.length
                  "
                ></v-checkbox>
              </v-list-item-action>
              <v-list-item-content><strong>All</strong></v-list-item-content>
            </v-list-item>

            <v-list-item
              v-bind="attrs"
              v-else
              dense
              class="px-2"
              v-on="on"
              #default="{ active }"
            >
              <v-list-item-action class="my-0 ml-0 mr-2">
                <v-checkbox dense :input-value="active"></v-checkbox>
              </v-list-item-action>
              <v-list-item-content>{{
                formatStateItem(state)
              }}</v-list-item-content>
            </v-list-item>
          </template>
        </v-overflow-btn>
      </template>
      <template #[`item.actions`]="{ item }">
        <v-btn
          v-if="item.hasUpcomingChange"
          color="purple"
          icon
          v-tooltip="{
            content: 'This product has an upcoming change',
            visible: $vuetifyDisplay.mdAndDown ? false : undefined
          }"
          @click="$emit('edit-product', item)"
        >
          <v-icon>mdi-tooltip-edit-outline</v-icon>
        </v-btn>

        <template v-else>
          <v-btn
            color="secondary"
            :disabled="item.hasUpcomingChange"
            icon
            v-tooltip="{
              content: 'Edit',
              visible: $vuetifyDisplay.mdAndDown ? false : undefined
            }"
            @click="$emit('edit-product', item)"
          >
            <v-icon>mdi-circle-edit-outline</v-icon>
          </v-btn>

          <v-btn
            color="orange"
            :disabled="item.hasUpcomingChange"
            icon
            v-tooltip="{
              content: 'Archive',
              visible: $vuetifyDisplay.mdAndDown ? false : undefined
            }"
            @click="$emit('archive-product', item)"
          >
            <v-icon>mdi-archive-outline</v-icon>
          </v-btn>
        </template>
      </template>
      <template #[`header.startAvailabilityDate`]="{ header }">
        {{ header.text }}
        <v-icon
          class="i-tooltip-color pb-1"
          small
          v-tooltip="{
            content: 'Date product first went live',
            visible: $vuetifyDisplay.mdAndDown ? false : undefined
          }"
        >
          mdi-help-circle-outline
        </v-icon>
      </template>
      <template #[`item.liveDate`]="{ item }">
        {{ $filters.formatDate(item.startAvailabilityDate) }}
      </template>

      <template v-slot:expanded-item="{ headers, item }">
        <td :colspan="headers.length">
          <i-product-details :item="item" :pricing-regions="pricingRegions" />
        </td>
      </template>
    </v-data-table>
    <v-alert
      v-if="hasAnyProductTag"
      dense
      text
      outlined
      border="left"
      elevation="1"
      type="info"
      class="mt-4 mx-3"
    >
      <div class="text-center">
        <strong>Promotion Tags:</strong>
        <span class="ml-1">
          <strong>S</strong> → Special&nbsp;&nbsp;| <strong>VB</strong> → Volume
          Buy&nbsp;&nbsp;| <strong>BG</strong> → Buy Get&nbsp;&nbsp;|
          <strong>P</strong> → Price Drop&nbsp;&nbsp;| <strong>FG</strong> →
          Family Group
        </span>
        <br />
        <small>
          These tags are displayed only when promotions are
          <strong>Live</strong> or <strong>Upcoming</strong>.
        </small>
      </div>
    </v-alert>
    <v-dialog v-model="showUpdatingPopup" max-width="520">
      <v-card>
        <v-card-title class="text-h6">Update in Progress</v-card-title>

        <v-card-text>
          Product changes are being processed in the current update timeframe.
          Please check again after it completes.
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn text color="primary" @click="showUpdatingPopup = false">
            OK
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import iProductDetails from '../products/ProductDetails'
import { formatStateLabel } from '@/utils/stateDisplay'
import { buildCountryDisplayRules } from '@/utils/countryDisplay'

export default {
  components: { iProductDetails },

  emits: [
    'add-product',
    'addProduct',
    'archive-product',
    'edit-product',
    'update-product-availability',
    'update:search'
  ],

  data: () => ({
    currentPage: 1,
    expandedProduct: [],
    showUpdatingPopup: false,
    headers: [
      { text: '', value: 'data-table-expand' },
      {
        text: 'CATEGORY',
        value: 'categoryProductHierarchy.category',
        align: 'start',
        filterable: true,
        width: '7%'
      },
      {
        text: 'SUB CATEGORY',
        value: 'categoryProductHierarchy.subCategory',
        align: 'start',
        filterable: true,
        width: '7%'
      },
      {
        text: 'SUPPLIER ITEM CODE',
        value: 'supplierItemId',
        align: 'start',
        filterable: true,
        width: '6%'
      },
      {
        text: 'BRAND',
        value: 'brand',
        align: 'start',
        filterable: true,
        width: '5%'
      },
      {
        text: 'PRODUCT DESCRIPTION',
        value: 'shortDescription',
        align: 'start',
        filterable: true
      },
      {
        text: 'CARTON WEIGHT',
        value: 'variableWeightTargetInKg',
        align: 'start',
        filterable: true,
        width: '6%'
      },
      {
        text: 'UNIT SIZE',
        value: 'unitQty',
        align: 'start',
        filterable: true,
        width: '5%'
      },
      {
        text: 'UNITS PER CARTON/PALLET',
        value: 'packageQuantity',
        align: 'start',
        filterable: false,
        class: 'units-per-carton',
        width: '6%'
      },
      {
        text: 'UNIT GTIN',
        value: 'baseUnitGtin',
        align: 'start',
        filterable: false,
        width: '6%'
      },
      {
        text: 'STOCK STATUS',
        value: 'availabilityStates',
        align: 'start',
        width: '180'
      },
      {
        text: 'LIVE DATE',
        value: 'startAvailabilityDate',
        align: 'start',
        width: '7%',
        filterable: true
      },
      {
        text: 'ACTIONS',
        value: 'actions',
        align: 'center',
        sortable: false,
        width: '95'
      }
    ]
  }),

  props: {
    loading: {
      type: Boolean,
      default: false
    },
    updatingAvailability: {
      type: Boolean,
      default: false
    },
    pageSize: {
      type: Number,
      default: -1
    },
    products: {
      type: Array,
      default: () => []
    },
    search: {
      type: String,
      default: ''
    },
    pricingRegions: {
      type: Array,
      default: () => []
    },
    supplierItemId: {
      type: String,
      default: null
    },
    tableHeight: {
      default: 'auto'
    },
    addProductHandler: {
      type: Function,
      required: true
    }
  },
  computed: {
    searchModel: {
      get() {
        return this.search
      },
      set(value) {
        this.$emit('update:search', value)
      }
    },
    ...mapGetters('tenant', ['tenantCountryRegion']),
    displayRules() {
      return buildCountryDisplayRules(this.tenantCountryRegion)
    },
    isUSCountry() {
      return this.displayRules.isUSCountry
    },
    hasAnyProductTag() {
      return this.products.some((p) => this.getPromoTags(p).length > 0)
    },

    isWithinTimeframe() {
      const now = new Date()

      const start = new Date()
      start.setHours(15, 0, 0, 0)

      const end = new Date()
      end.setHours(15, 30, 0, 0)

      return now >= start && now < end
    },

    shouldShowUpdatingPopup() {
      return (
        !this.loading &&
        (this.products?.length || 0) === 0 &&
        this.isWithinTimeframe
      )
    }
  },

  methods: {
    formatStateItem(stateCode) {
      return this.isUSCountry
        ? formatStateLabel(stateCode, this.tenantCountryRegion)
        : stateCode
    },
    expandProduct(products, supplierItemId) {
      const pIndex = products.findIndex(
        (p) => p.supplierItemId === supplierItemId
      )
      this.expandedProduct = [products[pIndex]]
      this.currentPage = Math.floor(pIndex / this.pageSize) + 1

      this.goToProduct(supplierItemId)
    },

    goToProduct(url) {
      // Finds HTML element so that the scroll built-in function can be used
      const element = Object.entries(this.$refs).find(
        (entry) => entry[0] === url.toString()
      )?.[1]

      if (element) element.scrollIntoView({ behavior: 'smooth' })
    },

    getPromoTags(product) {
      const tags = []

      if (product.familyGroup) tags.push('FG')

      const specials = (product.activeOrFuturePromotions || []).filter(
        (p) => !p?.toBeDeleted
      )

      const volumeBuys = (product.activeOrFutureVolumePromotions || []).filter(
        (p) => !p?.toBeDeleted
      )

      const buyGets = product.activeOrFutureBuyGetFreePromotions || []
      const priceDrops = (product.activeOrFuturePriceDrops || []).filter(
        (p) => !p?.toBeDeleted
      )

      if (specials.length > 0) tags.push('S')
      if (volumeBuys.length > 0) tags.push('VB')
      if (buyGets.length > 0) tags.push('BG')
      if (priceDrops.length > 0) tags.push('P')

      return tags
    },

    promotionTagColor(tag) {
      if (tag === 'S') return '#fe3d3d'
      if (tag === 'VB') return '#ffc400'
      if (tag === 'BG') return '#ffff06'
      if (tag === 'FG') return '#9c27b0'
      return '#4caf50'
    },

    toggleAllStates(product) {
      const allSelected =
        product.availabilityStates.length === product.rangedStates.length
      product.availabilityStates = allSelected ? [] : [...product.rangedStates]

      this.$emit('update-product-availability', {
        productCode: product.productCode,
        availabilityStates: product.availabilityStates
      })
    }
  },

  mounted() {},

  watch: {
    tableHeight() {
      if (this.supplierItemId) {
        this.expandProduct(this.products, this.supplierItemId)
      }
    },

    shouldShowUpdatingPopup: {
      immediate: true,
      handler(val) {
        this.showUpdatingPopup = val
      }
    }
  }
}
</script>

<style lang="scss" scoped>
#products-live {
  th,
  td {
    padding: 0px 10px;
  }
}

@media only screen and (min-width: 960px) {
  #products-live {
    th span {
      font-size: 13px;
    }
  }
}
</style>
<style lang="scss">
#products-live {
  .v-overflow-btn > .v-input__control .v-input__slot *,
  .v-overflow-btn > .v-input__control .v-field *,
  .v-overflow-btn .v-menu__content .v-list-item__content {
    font-size: 0.9rem !important;
  }

  .v-overflow-btn .v-overflow-btn__legacy-selection {
    color: rgba(0, 0, 0, 0.87);
    line-height: 1.5;
  }

  .v-overflow-btn .availability-status-count {
    font-size: 0.8rem;
  }

  .v-overflow-btn {
    &.p-btn-status--available > .v-input__control .v-input__slot,
    &.p-btn-status--available > .v-input__control .v-field {
      background: $green-color-bg;
      color: $green-color;
    }
    &.p-btn-status--partially-available > .v-input__control .v-input__slot,
    &.p-btn-status--partially-available > .v-input__control .v-field {
      background: $orange-color-bg;
      color: $orange-color;
    }
    &.p-btn-status--unavailable > .v-input__control .v-input__slot,
    &.p-btn-status--unavailable > .v-input__control .v-field {
      background: lightgray;
    }

    &.p-btn-status--available .v-field__input > input,
    &.p-btn-status--partially-available .v-field__input > input,
    &.p-btn-status--unavailable .v-field__input > input {
      opacity: 0;
      width: 0;
    }
  }
}
</style>
